import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { BRAND, NAV_LINKS } from "@/data/content";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

export const Navbar = ({ onStartProject }) => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => setOpen(false), [location.pathname]);

    return (
        <motion.header
            initial={{ y: -16, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="fixed top-3 left-0 right-0 z-50 px-3"
            data-testid="site-navbar"
        >
            <div
                className={`mx-auto w-full max-w-[1180px] rounded-full transition-all duration-300 ${
                    scrolled ? "glass-strong shadow-deep" : "glass shadow-soft"
                }`}
            >
                <div className="flex items-center justify-between gap-4 px-4 py-2 md:px-5 md:py-2.5">
                    <Link
                        to="/"
                        className="flex items-center -my-2 sm:-my-3"
                        data-testid="navbar-logo-link"
                    >
                        <img
                            src={BRAND.logoUrl}
                            alt="IdeaClap"
                            className="h-16 w-16 sm:h-20 sm:w-20 object-contain"
                        />
                    </Link>

                    <nav
                        className="hidden lg:flex items-center gap-1"
                        aria-label="Primary navigation"
                    >
                        {NAV_LINKS.map((link) => (
                            <NavLink
                                key={link.to}
                                to={link.to}
                                end={link.to === "/"}
                                data-testid={`nav-link-${link.label
                                    .toLowerCase()
                                    .replace(/\s/g, "-")}`}
                                className={({ isActive }) =>
                                    `relative px-3.5 py-2 text-[13.5px] tracking-tight transition-colors ${
                                        isActive
                                            ? "text-[hsl(var(--text))]"
                                            : "text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]"
                                    }`
                                }
                            >
                                {({ isActive }) => (
                                    <span className="inline-flex items-center gap-1.5">
                                        {link.label}
                                        {isActive && (
                                            <span className="inline-block h-1.5 w-1.5 rounded-full bg-[hsl(var(--gold))] shadow-[0_0_10px_rgba(187,118,33,0.55)]" />
                                        )}
                                    </span>
                                )}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={onStartProject}
                            data-testid="navbar-start-project-button"
                            className="sheen group hidden sm:inline-flex items-center gap-2 rounded-full bg-[hsl(var(--text))] hover:bg-[hsl(var(--text))]/90 text-[hsl(var(--bg))] px-4 py-2 text-[13.5px] font-semibold transition-colors duration-200"
                        >
                            Start a Project
                            <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
                        </button>

                        <Sheet open={open} onOpenChange={setOpen}>
                            <SheetTrigger asChild>
                                <button
                                    className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-full ring-1 ring-black/10 bg-white/60 text-[hsl(var(--text))]"
                                    aria-label="Open menu"
                                    data-testid="navbar-mobile-menu-button"
                                >
                                    <Menu className="h-4.5 w-4.5" />
                                </button>
                            </SheetTrigger>
                            <SheetContent
                                side="right"
                                className="w-[88vw] max-w-sm bg-[hsl(var(--bg))] border-l border-black/10 text-[hsl(var(--text))] p-6"
                            >
                                <div className="flex items-center justify-between">
                                    <Link
                                        to="/"
                                        className="flex items-center gap-2.5"
                                        onClick={() => setOpen(false)}
                                    >
                                        <img
                                            src={BRAND.logoUrl}
                                            alt="IdeaClap"
                                            className="h-12 w-12 object-contain"
                                        />
                                    </Link>
                                    <button
                                        onClick={() => setOpen(false)}
                                        className="h-9 w-9 inline-flex items-center justify-center rounded-full ring-1 ring-black/10"
                                        aria-label="Close menu"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                </div>
                                <nav className="mt-8 flex flex-col gap-1" aria-label="Mobile navigation">
                                    {NAV_LINKS.map((link) => (
                                        <NavLink
                                            key={link.to}
                                            to={link.to}
                                            end={link.to === "/"}
                                            data-testid={`mobile-nav-link-${link.label
                                                .toLowerCase()
                                                .replace(/\s/g, "-")}`}
                                            className={({ isActive }) =>
                                                `font-display text-2xl py-3 border-b border-black/10 ${
                                                    isActive
                                                        ? "text-[hsl(var(--text))]"
                                                        : "text-[hsl(var(--text-muted))]"
                                                }`
                                            }
                                        >
                                            {link.label}
                                        </NavLink>
                                    ))}
                                </nav>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setOpen(false);
                                        onStartProject?.();
                                    }}
                                    data-testid="mobile-navbar-start-project-button"
                                    className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[hsl(var(--text))] hover:bg-[hsl(var(--text))]/90 text-[hsl(var(--bg))] px-5 py-3 text-sm font-semibold transition-colors"
                                >
                                    Start a Project
                                    <ArrowRight className="h-4 w-4" />
                                </button>
                                <p className="mt-6 text-xs text-[hsl(var(--text-subtle))] leading-relaxed">
                                    {BRAND.contact.address}
                                    <br />
                                    {BRAND.contact.email} · {BRAND.contact.phone}
                                </p>
                            </SheetContent>
                        </Sheet>
                    </div>
                </div>
            </div>
        </motion.header>
    );
};

export default Navbar;
